import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import './SignatureBand.css';

interface SignatureBandProps {
  isHost: boolean;
  hostName: string;
  canStart: boolean;
  onStart: () => void;
}

/**
 * The verso's signature line. Sits at the foot of the left page where a
 * printed document's signature would. For the host: holds the Begin button
 * (the lobby's primary action). For non-hosts: holds a quiet italic line
 * acknowledging they're waiting on the host.
 *
 * Above the action sits a chapbook rule with a single seed-dot — the
 * page's literal signature line.
 */
export function SignatureBand({
  isHost,
  hostName,
  canStart,
  onStart,
}: SignatureBandProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="susdle-signature" data-can-start={canStart ? 'yes' : 'no'}>
      <SignatureRule />
      {isHost ? (
        <StartButton
          disabled={!canStart}
          canStart={canStart}
          onClick={onStart}
          label={t('lobby.start')}
        />
      ) : (
        <p className="susdle-signature__waiting">
          {t('lobby.waitingForHost', { name: hostName })}
        </p>
      )}
    </div>
  );
}

/**
 * Chapbook signature rule. The shared chapbook-divider ornament rendered as
 * a CSS mask painted by currentColor. The seed-dot beneath the Begin button
 * remains the consequence accent for the page-ready transition.
 */
function SignatureRule(): JSX.Element {
  return <span className="susdle-signature__rule" aria-hidden />;
}

function StartButton({
  disabled,
  canStart,
  onClick,
  label,
}: {
  disabled: boolean;
  canStart: boolean;
  onClick: () => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className="susdle-start"
      disabled={disabled}
      onClick={onClick}
    >
      <StartButtonFrame />
      <span className="susdle-start__label">{label}</span>
      {/* Oxblood seed-dot: the one place the consequence accent enters
          the lobby's resting state. Starting the round IS a consequence
          beat (irrevocable transition out of the lobby), and the seed-dot
          marks that without raising volume. Only present when canStart. */}
      {canStart ? <span aria-hidden className="susdle-start__seed" /> : null}
    </button>
  );
}

function StartButtonFrame(): JSX.Element {
  return (
    <svg
      className="susdle-start__frame"
      viewBox="0 0 220 56"
      aria-hidden
      focusable={false}
      preserveAspectRatio="none"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Chamfered chapbook cartouche. A softer 7px chamfer than the
            page-corner lozenges so the button reads as printed type-set
            chrome rather than a foliate ornament. Twin rules: outer at
            1.2 carries the frame weight, inner at 0.7 is a 4px-inset
            companion that densifies the cartouche. */}
        <path
          d="M 7 1 L 213 1 L 219 7 L 219 49 L 213 55 L 7 55 L 1 49 L 1 7 Z"
          strokeWidth={1.2}
        />
        <path
          d="M 11 5 L 209 5 L 215 11 L 215 45 L 209 51 L 11 51 L 5 45 L 5 11 Z"
          strokeWidth={0.7}
          opacity="0.55"
        />
      </g>
    </svg>
  );
}
